<template>
  <div id="screen" style="
  --bg: #050d1a;
  --panel: linear-gradient(180deg, rgba(14,38,72,.72), rgba(7,20,42,.72));
  --panel-solid: #0b1f3d;
  --border: rgba(0,212,255,.28);
  --border-soft: rgba(0,212,255,.14);
  --accent: #00d4ff;
  --accent-2: #2effc1;
  --text: #e8f4ff;
  --sub: #7d93b0;
  --dim: #566b87;
  --run: #2effc1;
  --wait: #ffb020;
  --off: #6b7c93;
  --ok: #2effa3;
">
    <!-- 顶部栏 -->
    <header class="top-bar">
      <div class="logo">
        <img class="logo-img" src="./logo.png" alt="印工社 Printer Cybertech">
      </div>
      <h1 class="title">印后车间数字驾驶舱</h1>
      <div class="clock"><b>{{ clockText }}</b></div>
    </header>

    <!-- 主面板 -->
    <main class="dashboard">
      <!-- 左侧列 -->
      <section class="col">
        <!-- OEE -->
        <div class="panel oee">
          <div class="ph">
            <div class="pt">车间时间稼动率</div>
            <div class="tag">今日</div>
          </div>
          <div class="body">
            <div class="ring">
              <svg viewBox="0 0 200 200">
                <defs>
                  <linearGradient id="oeegrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stop-color="#00d4ff"/>
                    <stop offset="100%" stop-color="#2effc1"/>
                  </linearGradient>
                </defs>
                <circle class="track" cx="100" cy="100" r="80" fill="none" stroke-width="11"/>
                <circle
                    class="bar"
                    cx="100" cy="100" r="80"
                    fill="none" stroke-width="11"
                    :style="{ strokeDashoffset: oeeDashOffset }"
                />
              </svg>
              <div class="center">
                <div class="v"><span>{{ oeeValue }}</span><small>%</small></div>
                <div class="l">时间稼动率 · 每日</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 环境 -->
        <div class="panel env">
          <div class="ph"><div class="pt">车间环境及能耗</div></div>
          <div class="grid">
            <div class="cell">
              <svg class="ico" viewBox="0 0 24 24" fill="none">
                <path d="M10 14V5a2 2 0 1 1 4 0v9a4 4 0 1 1-4 0z" stroke="currentColor" stroke-width="1.6"/>
                <circle cx="12" cy="16" r="1.6" fill="currentColor"/>
              </svg>
              <div class="num">24.4<small>°C</small></div>
              <div class="lab">车间温度</div>
            </div>
            <div class="cell">
              <svg class="ico" viewBox="0 0 24 24" fill="none">
                <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
              </svg>
              <div class="num">58.5<small>%</small></div>
              <div class="lab">车间湿度</div>
            </div>
            <div class="cell">
              <svg class="ico" viewBox="0 0 24 24" fill="none">
                <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
              </svg>
              <div class="num">68.8<small>kWh</small></div>
              <div class="lab">设备能耗</div>
            </div>
          </div>
        </div>

        <!-- 工序产出 -->
        <div class="panel proc">
          <div class="ph">
            <div class="pt">工序产出</div>
            <div class="tag">今日</div>
          </div>
          <div class="tbl">
            <table>
              <thead>
              <tr>
                <th>工序</th>
                <th>工序OEE</th>
                <th>今日产量</th>
                <th>单位</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>整纸</td>
                <td><span class="pct">88%<i><b style="width:88%"></b></i></span></td>
                <td class="na">—</td>
                <td class="na">—</td>
              </tr>
              <tr>
                <td>覆膜</td>
                <td><span class="pct">78%<i><b style="width:78%"></b></i></span></td>
                <td>17,800</td>
                <td>米</td>
              </tr>
              <tr>
                <td>上光</td>
                <td><span class="pct">75%<i><b style="width:75%"></b></i></span></td>
                <td>53,736</td>
                <td>张</td>
              </tr>
              <tr>
                <td>烫金</td>
                <td><span class="pct">70%<i><b style="width:70%"></b></i></span></td>
                <td>49,939</td>
                <td>张</td>
              </tr>
              <tr>
                <td>裱纸</td>
                <td><span class="pct">74%<i><b style="width:74%"></b></i></span></td>
                <td>22,560</td>
                <td>米</td>
              </tr>
              <tr>
                <td>模切</td>
                <td><span class="pct">72%<i><b style="width:72%"></b></i></span></td>
                <td>27,648</td>
                <td>张</td>
              </tr>
              <tr>
                <td>清废</td>
                <td><span class="pct">86%<i><b style="width:86%"></b></i></span></td>
                <td class="na">—</td>
                <td class="na">—</td>
              </tr>
              <tr>
                <td>糊盒</td>
                <td><span class="pct">76%<i><b style="width:76%"></b></i></span></td>
                <td>122,655</td>
                <td>个</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- 中间列 -->
      <section class="col">
        <!-- 总产量 -->
        <div class="panel output">
          <div class="ph"><div class="pt">今日总产量</div></div>
          <div class="body">
            <div class="num">{{ totalOutput }}</div>
            <div class="meta"><div class="t">个</div></div>
            <svg class="spark" id="spark" viewBox="0 0 360 120" preserveAspectRatio="none"></svg>
          </div>
        </div>

        <!-- 订单 -->
        <!-- 订单 -->
        <div class="panel order">
          <div class="ph">
            <div class="pt">订单信息</div>
            <div class="tag">今日订单</div>
          </div>

          <div class="order-scroll-container">
            <!-- 固定表头 -->
            <div class="order-head-wrapper">
              <table class="order-table">
                <colgroup>
                  <col style="width:16%">
                  <col style="width:21%">
                  <col style="width:19%">
                  <col style="width:10%">
                  <col style="width:11%">
                  <col style="width:12%">
                  <col style="width:11%">
                </colgroup>
                <thead>
                <tr>
                  <th>订单号</th>
                  <th>客户</th>
                  <th>产品</th>
                  <th>订单量</th>
                  <th>交期</th>
                  <th>状态</th>
                  <th>当前工序</th>
                </tr>
                </thead>
              </table>
            </div>

            <!-- 滚动内容 -->
            <div class="order-body-scroll">
              <div class="scroll-content">
                <table class="order-table">
                  <colgroup>
                    <col style="width:16%">
                    <col style="width:21%">
                    <col style="width:19%">
                    <col style="width:10%">
                    <col style="width:11%">
                    <col style="width:12%">
                    <col style="width:11%">
                  </colgroup>
                  <tbody>
                  <tr><td>SO26070119</td><td>青岛鸿远科技发展</td><td>工业零部件重型彩箱</td><td>10,000</td><td>07-10</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070120</td><td>青岛盛达贸易</td><td>节日礼品手提彩盒</td><td>20,000</td><td>07-11</td><td><span class="badge run">生产中</span></td><td>烫金</td></tr>
                  <tr><td>SO26070124</td><td>青岛优品礼品</td><td>化妆品套装彩盒</td><td>4,500</td><td>07-15</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070126</td><td>青岛芯光电子科技</td><td>电子产品通用彩盒</td><td>3,000</td><td>07-21</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070127</td><td>青岛百味佳食品</td><td>精品月饼彩盒</td><td>2,600</td><td>07-25</td><td><span class="badge run">生产中</span></td><td>糊盒</td></tr>
                  <tr><td>SO26070121</td><td>青岛清泉饮品</td><td>红酒礼盒彩箱</td><td>30,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070122</td><td>青岛乐福保健品</td><td>医药保健品彩盒</td><td>34,000</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070123</td><td>青岛雅韵服饰</td><td>鞋类换季收纳彩箱</td><td>45,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>烫金</td></tr>
                  <tr><td>SO26070125</td><td>青岛康惠医药</td><td>医药保健品彩盒</td><td>5,600</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070128</td><td>济南舜耕茶业</td><td>茶叶精品礼盒</td><td>1,800</td><td>07-18</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070129</td><td>烟台海德水产食品</td><td>海鲜西点保鲜彩盒</td><td>6,500</td><td>07-14</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070130</td><td>潍坊风筝文化创意</td><td>风筝文创礼品彩盒</td><td>2,600</td><td>07-19</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070131</td><td>淄博华光陶瓷制品</td><td>儿童玩具彩箱</td><td>22,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>覆膜</td></tr>
                  <tr><td>SO26070132</td><td>临沂商城供应链管理</td><td>内衣精品彩盒</td><td>1,200</td><td>07-20</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070133</td><td>济宁圣源中药饮片</td><td>中药饮片彩盒</td><td>7,200</td><td>07-15</td><td><span class="badge run">生产中</span></td><td>覆膜</td></tr>
                  <tr><td>SO26070134</td><td>泰安泰山旅游纪念品</td><td>文具套装彩盒</td><td>2,300</td><td>07-18</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070135</td><td>德州永盛斋扒鸡食品</td><td>扒鸡熟食周转彩箱</td><td>28,000</td><td>07-12</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070136</td><td>聊城鑫鹏钢管制造</td><td>钢管配件收纳彩罐</td><td>5,600</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070137</td><td>滨州瑞丰家纺</td><td>家纺配套彩盒</td><td>4,100</td><td>07-17</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  </tbody>
                </table>

                <!-- 复制一份实现无缝循环 -->
                <table class="order-table">
                  <colgroup>
                    <col style="width:16%">
                    <col style="width:21%">
                    <col style="width:19%">
                    <col style="width:10%">
                    <col style="width:11%">
                    <col style="width:12%">
                    <col style="width:11%">
                  </colgroup>
                  <tbody>
                  <tr><td>SO26070119</td><td>青岛鸿远科技发展</td><td>工业零部件重型彩箱</td><td>10,000</td><td>07-10</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070120</td><td>青岛盛达贸易</td><td>节日礼品手提彩盒</td><td>20,000</td><td>07-11</td><td><span class="badge run">生产中</span></td><td>烫金</td></tr>
                  <tr><td>SO26070124</td><td>青岛优品礼品</td><td>化妆品套装彩盒</td><td>4,500</td><td>07-15</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070126</td><td>青岛芯光电子科技</td><td>电子产品通用彩盒</td><td>3,000</td><td>07-21</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070127</td><td>青岛百味佳食品</td><td>精品月饼彩盒</td><td>2,600</td><td>07-25</td><td><span class="badge run">生产中</span></td><td>糊盒</td></tr>
                  <tr><td>SO26070121</td><td>青岛清泉饮品</td><td>红酒礼盒彩箱</td><td>30,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070122</td><td>青岛乐福保健品</td><td>医药保健品彩盒</td><td>34,000</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070123</td><td>青岛雅韵服饰</td><td>鞋类换季收纳彩箱</td><td>45,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>烫金</td></tr>
                  <tr><td>SO26070125</td><td>青岛康惠医药</td><td>医药保健品彩盒</td><td>5,600</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070128</td><td>济南舜耕茶业</td><td>茶叶精品礼盒</td><td>1,800</td><td>07-18</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070129</td><td>烟台海德水产食品</td><td>海鲜西点保鲜彩盒</td><td>6,500</td><td>07-14</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070130</td><td>潍坊风筝文化创意</td><td>风筝文创礼品彩盒</td><td>2,600</td><td>07-19</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070131</td><td>淄博华光陶瓷制品</td><td>儿童玩具彩箱</td><td>22,000</td><td>07-13</td><td><span class="badge run">生产中</span></td><td>覆膜</td></tr>
                  <tr><td>SO26070132</td><td>临沂商城供应链管理</td><td>内衣精品彩盒</td><td>1,200</td><td>07-20</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070133</td><td>济宁圣源中药饮片</td><td>中药饮片彩盒</td><td>7,200</td><td>07-15</td><td><span class="badge run">生产中</span></td><td>覆膜</td></tr>
                  <tr><td>SO26070134</td><td>泰安泰山旅游纪念品</td><td>文具套装彩盒</td><td>2,300</td><td>07-18</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  <tr><td>SO26070135</td><td>德州永盛斋扒鸡食品</td><td>扒鸡熟食周转彩箱</td><td>28,000</td><td>07-12</td><td><span class="badge run">生产中</span></td><td>上光</td></tr>
                  <tr><td>SO26070136</td><td>聊城鑫鹏钢管制造</td><td>钢管配件收纳彩罐</td><td>5,600</td><td>07-16</td><td><span class="badge done">已完工</span></td><td>—</td></tr>
                  <tr><td>SO26070137</td><td>滨州瑞丰家纺</td><td>家纺配套彩盒</td><td>4,100</td><td>07-17</td><td><span class="badge wait">未生产</span></td><td>—</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 右侧设备 -->
      <section class="col col-devices">
        <!-- 整纸机 -->
        <div
            class="panel device"
            :class="deviceZhengzhi.statusClass"
        >
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="13" rx="1.5" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M3 20h18M9 4V2M15 4V2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
              </span>
              整纸机
              <span class="model">{{ deviceZhengzhi.type }}</span>
            </div>
            <span class="dot-status wait"><i></i>待机</span>
          </div>
          <div class="mtr" style="margin-top: 20px">
            <div class="it">
              <div class="lb">时间稼动率</div>
              <div class="vl" v-html="deviceZhengzhi.timeOee"></div>
            </div>
            <div class="it">
              <div class="lb">运行时长</div>
              <div class="vl" v-html="deviceZhengzhi.time"></div>
            </div>
          </div>
        </div>

        <!-- 覆膜机 -->
        <div
            class="panel device"
            :class="deviceFumo.statusClass"
        >
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.6"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6"/>
                </svg>
              </span>
              覆膜机
              <span class="model">{{ deviceFumo.type }}</span>
            </div>
            <span class="dot-status run">
              <i></i>运行中
            </span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl" v-html="deviceFumo.timeOee"></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl" v-html="deviceFumo.xingNengOee"></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl" v-html="deviceFumo.time"></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl" v-html="deviceFumo.count"></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl" v-html="deviceFumo.speed"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 上光机 -->
        <div class="panel device st-run">
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
              </span>
              上光机
              <span class="model">祥鹏 XP-1020</span>
            </div>
            <span class="dot-status run"><i></i>运行中</span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl">80<small>%</small></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl">93.5<small>%</small></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl">7.2<small>小时</small></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl">53,736<small>张</small></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl">8,000<small>张/小时</small></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 烫金机 -->
        <div class="panel device st-run">
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l2.4 5.8L20 8l-4.5 4 1.3 6L12 15l-4.8 3 1.3-6L4 8l5.6-.2L12 2z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                </svg>
              </span>
              烫金机
              <span class="model">有恒 YG-9MA</span>
            </div>
            <span class="dot-status run"><i></i>运行中</span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl">76<small>%</small></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl">91.8<small>%</small></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl">6.8<small>小时</small></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl">49,939<small>张</small></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl">8,000<small>张/小时</small></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 裱纸机 -->
        <div class="panel device st-wait">
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="8" width="13" height="10" rx="1" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M8 8V6h13v10" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                </svg>
              </span>
              裱纸机
              <span class="model">劲豹 JP-1450</span>
            </div>
            <span class="dot-status run">
              <i></i>运行中
            </span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl">82<small>%</small></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl">94<small>%</small></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl">4.0<small>小时</small></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl">22,560<small>米</small></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl">100<small>米/分钟</small></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 模切机 -->
        <div
            class="panel device"
            :class="deviceMoqie.statusClass"
        >
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 7h14l4 5-4 5H3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  <circle cx="8" cy="12" r="1.4" fill="currentColor"/>
                </svg>
              </span>
              模切机
              <span class="model">{{ deviceMoqie.type }}</span>
            </div>
            <span class="dot-status run">
              <i></i>运行中
            </span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl" v-html="deviceMoqie.timeOee"></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl" v-html="deviceMoqie.xingNengOee"></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl" v-html="deviceMoqie.time"></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl" v-html="deviceMoqie.count"></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl" v-html="deviceMoqie.speed"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 清废机 -->
        <div
            class="panel device"
            :class="deviceQingfei.statusClass"
        >
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="6" cy="6" r="2.4" stroke="currentColor" stroke-width="1.6"/>
                  <circle cx="6" cy="18" r="2.4" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M8.2 7.6 20 17M8.2 16.4 20 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
              </span>
              清废机
              <span class="model">{{ deviceQingfei.type }}</span>
            </div>
            <span class="dot-status wait"><i></i>待机</span>
          </div>
          <div class="mtr" style="margin-top: 20px">
            <div class="it">
              <div class="lb">时间稼动率</div>
              <div class="vl" v-html="deviceQingfei.timeOee"></div>
            </div>
            <div class="it">
              <div class="lb">运行时长</div>
              <div class="vl" v-html="deviceQingfei.time"></div>
            </div>
          </div>
        </div>

        <!-- 糊盒机 -->
        <div class="panel device st-run">
          <div class="dh">
            <div class="nm">
              <span class="gi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  <path d="M3 8l9 5 9-5M12 13v8" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                </svg>
              </span>
              糊盒机
              <span class="model">长荣 CH-450</span>
            </div>
            <span class="dot-status run"><i></i>运行中</span>
          </div>
          <div class="mtr c23">
            <div class="r2">
              <div class="it">
                <div class="lb">时间稼动率</div>
                <div class="vl">79<small>%</small></div>
              </div>
              <div class="it">
                <div class="lb">性能稼动率</div>
                <div class="vl">96.2<small>%</small></div>
              </div>
            </div>
            <div class="r3">
              <div class="it">
                <div class="lb">运行时长</div>
                <div class="vl">8.5<small>小时</small></div>
              </div>
              <div class="it">
                <div class="lb">今日产量</div>
                <div class="vl">122,655<small>个</small></div>
              </div>
              <div class="it">
                <div class="lb">速度</div>
                <div class="vl">15,000<small>个/小时</small></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'Dashboard',
  data() {
    return {
      clockText: '',
      oeeValue: 0,
      oeeDashOffset: '502.65',
      totalOutput: '0',
      scrollPos: 0,
      scrollTimer: null,
      orderScrollHeight: 0,
      orderScrollPos: 0,

      // 4台接口设备
      deviceZhengzhi: {
        statusClass: 'st-off',
        statusText: '离线',
        type: '威能 WPT-127A',
        timeOee: '88<small>%</small>',
        time: '6.5<small>小时</small>'
      },
      deviceFumo: {
        statusClass: 'st-run',
        statusText: '运行中',
        type: '天岑 HFM-1450LRW',
        timeOee: '82<small>%</small>',
        xingNengOee: '95.1<small>%</small>',
        time: '7.8<small>小时</small>',
        count: '17,800<small>米</small>',
        speed: '2,400<small>米/小时</small>'
      },
      deviceMoqie: {
        statusClass: 'st-wait',
        statusText: '待机',
        type: '欧诺 WH-1050SF',
        timeOee: '75<small>%</small>',
        xingNengOee: '96<small>%</small>',
        time: '4.5<small>小时</small>',
        count: '27,648<small>张</small>',
        speed: '6,400<small>张/小时</small>'
      },
      deviceQingfei: {
        statusClass: 'st-run',
        statusText: '运行中',
        type: '快易达 KYD-1080A',
        timeOee: '86<small>%</small>',
        time: '7.2<small>小时</small>'
      }
    }
  },
  mounted() {
    this.startClock()
    this.drawSpark()

    setTimeout(() => {
      this.doOeeAnimation(81)
      this.countTotal(122655)
      // this.startScroll() // 加这一行
    }, 150)
  },
  beforeDestroy() {
    clearInterval(this.scrollTimer)
  },
  methods: {
    // 时钟
    pad(n) {
      return n < 10 ? '0' + n : n
    },
    startClock() {
      const tick = () => {
        const d = new Date()
        const h = d.getHours()
        const shift = h >= 8 && h < 20 ? '白班' : '夜班'
        const s = `${shift} ${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())} ${this.pad(h)}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}`
        this.clockText = s
      }
      tick()
      setInterval(tick, 1000)
    },

    // OEE圆环动画
    doOeeAnimation(val) {
      const C = 2 * Math.PI * 80
      this.oeeDashOffset = C * (1 - val / 100) + 'px'
      this.countUp(val, 1600, v => {
        this.oeeValue = v
      })
    },

    // 数字滚动
    countUp(target, dur, cb) {
      let start = null
      const step = ts => {
        if (!start) start = ts
        const p = Math.min((ts - start) / dur, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        const v = Math.round(target * ease)
        cb(v)
        if (p < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },

    // 总产量
    countTotal(num) {
      this.countUp(num, 1800, v => {
        this.totalOutput = v.toLocaleString()
      })
    },

    // 产量曲线
    drawSpark() {
      const svg = document.getElementById('spark')
      const pts = [40, 52, 46, 60, 55, 68, 62, 78, 72, 85, 80, 96]
      const w = 360, h = 120, n = pts.length
      const stepX = w / (n - 1)
      const max = 100, min = 30
      const coords = pts.map((p, i) => [
        i * stepX,
        h - ((p - min) / (max - min)) * h
      ])
      const line = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ')
      const area = line + ` L${w} ${h} L0 ${h} Z`
      svg.innerHTML = `
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#2effc1" stop-opacity="0.45"/>
            <stop offset="100%" stop-color="#2effc1" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <path d="${area}" fill="url(#sg)"/>
        <path d="${line}" fill="none" stroke="#2effc1" stroke-width="2" stroke-linejoin="round" style="filter:drop-shadow(0 0 4px rgba(46,255,193,.6))"/>
        ${coords.map(c => `<circle cx="${c[0].toFixed(1)}" cy="${c[1].toFixed(1)}" r="2" fill="#2effc1"/>`).join('')}
      `
    },

    // 接口请求
    async fetchData() {
      try {
        const res = await fetch('http://193.168.1.50:9201/psl/user/getDaPingJiTaiZhanHui?teamId=c41a0ac6-4fd8-4bc7-b5ca-e8c20177a882')
        const data = await res.json()
        const list = data?.message?.data?.orderInfos || []
        const devices = data?.message?.data?.deviceInfos || []
        this.renderDevices(devices)
      } catch (err) {
        console.error('接口请求失败', err)
      }
    },
    // 格式化数字
    formatNum(val) {
      if (val == null || isNaN(val)) return '—'
      return Number(val).toLocaleString('zh-CN', { maximumFractionDigits: 1 })
    },

    // 渲染4台设备
    renderDevices(list) {
      const map = {
        '整纸机': 'zhengzhi',
        '覆膜机': 'fumo',
        '模切机': 'moqie',
        '清废机': 'qingfei'
      }
      list.forEach(dev => {
        const key = map[dev.name]
        if (!key) return

        let statusClass, statusText
        if (dev.state === 1) {
          statusClass = 'st-run'
          statusText = '运行中'
        } else if (dev.state === 0) {
          statusClass = 'st-wait'
          statusText = '待机'
        } else {
          statusClass = 'st-off'
          statusText = '离线'
        }

        const target = this[`device${key.charAt(0).toUpperCase() + key.slice(1)}`]
        target.statusClass = statusClass
        target.statusText = statusText
        target.type = dev.type || ''

        target.timeOee = `${this.formatNum(dev.timeOee)}<small>%</small>`
        if (dev.xingNengOee !== undefined) {
          target.xingNengOee = `${this.formatNum(dev.xingNengOee)}<small>%</small>`
        }
        target.time = `${this.formatNum(dev.time)}<small>小时</small>`

        if (dev.count !== undefined) {
          const unit = key === 'fumo' ? '米' : key === 'moqie' ? '张' : ''
          target.count = `${this.formatNum(dev.count)}<small>${unit}</small>`
        }
        if (dev.speed !== undefined) {
          const unit = key === 'fumo' ? '米/小时' : key === 'moqie' ? '张/小时' : ''
          target.speed = `${this.formatNum(dev.speed)}<small>${unit}</small>`
        }
      })
    },
    // 订单表自动滚动
    startScroll() {
      this.stopScroll();
      const tbl = document.querySelector('.order .tbl');
      if (!tbl) return;

      this.scrollTimer = setInterval(() => {
        this.orderScrollPos += 0.8; // 滚动速度
        const max = tbl.scrollHeight - tbl.clientHeight;
        if (this.orderScrollPos >= max) {
          this.orderScrollPos = 0;
        }
        tbl.scrollTop = this.orderScrollPos;
      }, 30);
    },
    stopScroll() {
      clearInterval(this.scrollTimer);
    },
  }
}
</script>

<style scoped lang="scss">
:root{
  --bg:#050d1a;
  --panel:linear-gradient(180deg, rgba(14,38,72,.72), rgba(7,20,42,.72));
  --panel-solid:#0b1f3d;
  --border:rgba(0,212,255,.28);
  --border-soft:rgba(0,212,255,.14);
  --accent:#00d4ff;
  --accent-2:#2effc1;
  --text:#e8f4ff;
  --sub:#7d93b0;
  --dim:#566b87;
  --run:#2effc1;
  --wait:#ffb020;
  --off:#6b7c93;
  --ok:#2effa3;
}
*{margin:0;padding:0;box-sizing:border-box;}
html,body{width:100%;height:100%;overflow:hidden;margin:0;font-family:"Microsoft YaHei","微软雅黑","Source Han Sans CN","PingFang SC",sans-serif;color:#e8f4ff;}
body{
  background:
      radial-gradient(ellipse at 18% -10%, rgba(0,120,200,.22), transparent 55%),
      radial-gradient(ellipse at 85% 110%, rgba(0,200,255,.16), transparent 55%),
      var(--bg);
}

#screen{
  width:100%;height:100vh;
  padding:22px 16px 26px;
  display:flex;flex-direction:column;
  position:relative;
  background-color: #050d1a;
}
#screen::before{
  content:"";position:absolute;inset:0;pointer-events:none;
  height: 100vh;
  background-image:
      linear-gradient(rgba(0,212,255,.045) 1px,transparent 1px),
      linear-gradient(90deg, rgba(0,212,255,.045) 1px,transparent 1px);
  background-size:46px 46px;
  -webkit-mask-image:radial-gradient(ellipse at center,#000 55%,transparent 100%);
  mask-image:radial-gradient(ellipse at center,#000 55%,transparent 100%);
}

.top-bar{
  position:relative;height:74px;flex:0 0 74px;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 20px;margin-bottom:16px;
  background:linear-gradient(90deg, rgba(0,212,255,.10), rgba(0,212,255,0) 30%, rgba(0,212,255,0) 70%, rgba(0,212,255,.10));
  border-top:1px solid var(--border);
  box-shadow:0 4px 18px rgba(0,0,0,.35);
}
.top-bar::before,.top-bar::after{
  content:"";position:absolute;top:-1px;width:260px;height:2px;
  background:linear-gradient(90deg, transparent, var(--accent), transparent);
}
.top-bar::before{left:50%;transform:translateX(-50%);}
.logo{display:flex;align-items:center;gap:14px;}
.logo-img{height:50px;width:auto;mix-blend-mode:lighten;}
.logo .mark{
  width:40px;height:40px;
  display:flex;align-items:center;justify-content:center;
  filter:drop-shadow(0 0 6px rgba(255,196,0,.45));
}
.logo .mark svg{width:40px;height:40px;}
.logo .txt b{display:block;font-size:17px;}
.logo .txt span{display:block;font-size:9px;color:var(--sub);margin-top:2px;}
.title{
  position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  font-size:28px;font-weight:700;
  background:linear-gradient(180deg,#ffffff,#7fdcff);
  -webkit-background-clip:text;background-clip:text;color:transparent;
  text-shadow:0 0 24px rgba(0,212,255,.35);
}
.clock{display:flex;align-items:center;gap:10px;font-size:14px;color:var(--sub);transform:translate(-8px,3px);}
.clock .dot{width:9px;height:9px;border-radius:50%;background:var(--accent);box-shadow:0 0 10px var(--accent);animation:blink 1.2s infinite;}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.25}}
.clock b{color:#e8f4ff;font-weight:500;font-variant-numeric:tabular-nums;}

.dashboard{
  flex:1;display:grid;gap:16px;min-height:0;
  grid-template-columns:370px minmax(0,1fr) 660px;
}
.col{display:flex;flex-direction:column;gap:16px;min-height:0;}

.panel{
  position:relative;
  background:var(--panel);
  border:1px solid var(--border-soft);
  border-radius:6px;
  padding:18px 20px;
  box-shadow:inset 0 0 28px rgba(0,212,255,.05), 0 4px 18px rgba(0,0,0,.35);
}
.panel::before,.panel::after{
  content:"";position:absolute;width:14px;height:14px;border:2px solid var(--accent);
}
.panel::before{top:-1px;left:-1px;border-right:0;border-bottom:0;}
.panel::after{bottom:-1px;right:-1px;border-left:0;border-top:0;}
.panel .ph{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
.panel .ph .pt{display:flex;align-items:center;gap:10px;font-size:16px;font-weight:600;color: #e8f4ff;}
.panel .ph .pt::before{content:"";width:4px;height:16px;background:var(--accent);box-shadow:0 0 8px var(--accent);}
.panel .ph .tag{font-size:10px;color:var(--accent);border:1px solid var(--border);padding:2px 10px;border-radius:10px;}

.oee{flex:0 0 206px;}
.oee .body{display:flex;align-items:center;justify-content:center;height:calc(100% - 44px);}
.ring{position:relative;width:210px;height:210px;}
.ring svg{width:100%;height:100%;transform:rotate(-90deg);}
.ring .track{stroke:rgba(0,212,255,.12);}
.ring .bar{stroke:url(#oeegrad);stroke-linecap:round;filter:drop-shadow(0 0 6px rgba(0,212,255,.6));
  stroke-dasharray:502.65;stroke-dashoffset:502.65;transition:stroke-dashoffset 1.6s cubic-bezier(.22,.9,.3,1);}
.ring .center{position:absolute;display:flex;flex-direction:column;align-items:center;justify-content:center; top: 27%;
  left: 22%;}
.ring .center .v{font-size:54px;font-weight:700;line-height:1;background:linear-gradient(180deg,#fff,#7fdcff);-webkit-background-clip:text;background-clip:text;color:transparent;font-variant-numeric:tabular-nums;}
.ring .center .v small{font-size:24px;}
.ring .center .l{margin-top:8px;font-size:9px;color:var(--sub);}

.env{flex:0 0 220px;}
.env .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;height:calc(100% - 44px);}
.env .cell{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;border:1px solid var(--border-soft);border-radius:5px;background:rgba(0,212,255,.04);}
.env .cell .ico{width:34px;height:34px;color:var(--accent);}
.env .cell .num{font-size:24px;font-weight:700;color:#e8f4ff;font-variant-numeric:tabular-nums;}
.env .cell .num small{font-size:12px;color:var(--sub);font-weight:400;margin-left:2px;}
.env .cell .lab{font-size:12px;color:var(--sub);}

table{width:100%;border-collapse:collapse;font-size:13px;}
thead th{
  background:rgba(0,212,255,.08);color:#bfe6ff;font-weight:600;
  padding:9px 10px;text-align:left;font-size:12px;
  border-bottom:1px solid var(--border);
}
tbody td{padding:10px;border-bottom:1px solid rgba(0,212,255,.07);color:#e8f4ff;}
tbody tr:hover{background:rgba(0,212,255,.06);}
tbody tr:last-child td{border-bottom:0;}

.proc{flex:1 1 0;display:flex;flex-direction:column;}
.proc .tbl{display:flex;align-items:center;overflow:hidden;}
.proc .tbl table{width:100%;}
.proc table thead th{text-align:center;padding:6px 8px;}
.proc table tbody td{text-align:center;padding:5px 8px;}
.proc table tbody td.na{color:var(--dim);}
.proc table tbody td:first-child{text-align:center;color:var(--accent);}
.pct{display:inline-flex;align-items:center;gap:6px;}
.pct i{display:inline-block;width:42px;height:5px;border-radius:3px;background:rgba(0,212,255,.15);overflow:hidden;}
.pct i b{display:block;height:100%;background:linear-gradient(90deg,var(--accent),var(--accent-2));}
.deco-bar{display:flex;align-items:center;gap:10px;padding:12px 16px;border:1px solid var(--border-soft);border-radius:6px;background:linear-gradient(90deg,rgba(0,212,255,.07),transparent);font-size:12px;color:var(--sub);}
.deco-bar .pulse{width:8px;height:8px;border-radius:50%;background:var(--accent-2);box-shadow:0 0 8px var(--accent-2);animation:blink 1.2s infinite;}

.output{flex:0 0 232px;}
.output .body{height:calc(100% - 44px);display:flex;align-items:center;justify-content:center;gap:60px;}
.output .num{font-size:64px;font-weight:700;line-height:1;font-variant-numeric:tabular-nums;
  background:linear-gradient(180deg,#ffffff 30%,#2effc1);-webkit-background-clip:text;background-clip:text;color:transparent;
  text-shadow:0 0 30px rgba(46,255,193,.3);}
.output .meta{text-align:left;}
.output .meta .t{font-size:16px;color:#e8f4ff;}
.output .meta .s{font-size:12px;color:var(--sub);margin-top:8px;}
.output .spark{flex:1;max-width:360px;height:120px;}

.order{flex:1 1 auto;min-height:0;}
.order .tbl{height:calc(100% - 44px);overflow-y:auto;}
.order .tbl::-webkit-scrollbar{width:6px;}
.order .tbl::-webkit-scrollbar-thumb{background:rgba(0,212,255,.3);border-radius:3px;}
.order .tbl::-webkit-scrollbar-thumb:hover{background:rgba(0,212,255,.5);}
.order .tbl::-webkit-scrollbar-track{background:transparent;}
.order table{table-layout:fixed;}
.order table thead th{position:sticky;top:0;z-index:1;font-size:12px;padding:11px 8px;background:#0c1d38;}
.order table tbody td{padding:10px 8px;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.order table tbody td:first-child{color:var(--accent);font-weight:600;font-family:"Consolas","SF Mono","Menlo",monospace;font-size:11px;}
.order table thead th:nth-child(4),
.order table thead th:nth-child(5),
.order table thead th:nth-child(6),
.order table thead th:nth-child(7){text-align:center;}
.order table tbody td:nth-child(4),
.order table tbody td:nth-child(5),
.order table tbody td:nth-child(6),
.order table tbody td:nth-child(7){text-align:center;}
// 订单 CSS 无缝滚动
// 订单 CSS 纯 CSS 无缝自动滚动（只滚 tbody）
/* 订单容器 */
.order-scroll-container {
  height: calc(100% - 44px);
  overflow: hidden;
  position: relative;
  //mask-image: linear-gradient(to bottom, transparent 0%, #000 10%, #000 90%, transparent 100%);
  //-webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 10%, #000 90%, transparent 100%);
}

/* 统一表格样式 */
.order-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size:11px !important;
}

/* 固定表头 */
.order-head-wrapper {
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 10;
}
.order-head-wrapper th {
  background: #0c1d38;
  font-size: 11px;
  padding: 11px 8px;
  text-align: left;
  color: #bfe6ff;
  font-weight: 600;
  border-bottom: 1px solid var(--border);
}

/* 滚动区域 */
.order-body-scroll {
  width: 100%;
  overflow: hidden;
}
.scroll-content {
  width: 100%;
  animation: orderScroll 30s linear infinite;
}
.scroll-content:hover {
  animation-play-state: paused;
}
@keyframes orderScroll {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

/* 单元格统一样式 */
.order-table tbody td {
  padding: 10px 8px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #e8f4ff;
  border-bottom: 1px solid rgba(0,212,255,.07);
}
.order-table tbody tr:hover {
  background: rgba(0,212,255,.06);
}
.order-table td:nth-child(4),
.order-table td:nth-child(5),
.order-table td:nth-child(6),
.order-table td:nth-child(7),
.order-table th:nth-child(4),
.order-table th:nth-child(5),
.order-table th:nth-child(6),
.order-table th:nth-child(7) {
  text-align: center;
}
.order-table td:first-child {
  color: var(--accent);
  font-weight: 600;
  font-family: Consolas, monospace;
  font-size: 11px;
}
.badge{display:inline-block;padding:3px 12px;border-radius:11px;font-size:11px;line-height:1.4;}
.badge.run{color:var(--accent);background:rgba(0,212,255,.14);border:1px solid rgba(0,212,255,.4);}
.badge.wait{color:var(--wait);background:rgba(255,176,32,.12);border:1px solid rgba(255,176,32,.4);}
.badge.done{color:var(--ok);background:rgba(46,255,163,.12);border:1px solid rgba(46,255,163,.4);}
.badge.off{color:var(--off);background:rgba(107,124,147,.12);border:1px solid rgba(107,124,147,.4);}

.device{flex:1 1 0;min-height:0;display:flex;flex-direction:column;}
.device .dh{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
.device .dh .nm{display:flex;align-items:center;gap:10px;font-size:17px;font-weight:600;}
.device .dh .nm .model{font-size:11px;color:var(--sub);font-weight:400;white-space:nowrap;}
.device .dh .nm .gi{width:30px;height:30px;border-radius:6px;background:rgba(0,212,255,.1);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--accent);}
.device .dh .nm .gi svg{width:18px;height:18px;}
.device .mtr{display:grid;grid-template-columns:repeat(3,1fr);gap:12px 16px;flex:1;}
.device .mtr.c23{display:flex;flex-direction:column;justify-content:center;gap:14px;}
.device .mtr.c23 .r2{display:grid;grid-template-columns:1.2fr 1.25fr 1.4fr;gap:16px;margin-bottom: 20px}
.device .mtr.c23 .r3{display:grid;grid-template-columns:1.2fr 1.25fr 1.4fr;gap:12px;}
.device .mtr .it{display:flex;flex-direction:column;gap:4px;}
.device .mtr .it .lb{font-size:11px;color:var(--sub);margin-bottom: 5px}
.device .mtr .it .vl{font-size:20px;font-weight:600;color:#e8f4ff;font-variant-numeric:tabular-nums;}
.device .mtr .it .vl small{font-size:12px;color:var(--sub);font-weight:400;margin-left:3px;}

.device.st-off{opacity:.86;}

.dot-status{display:inline-flex;align-items:center;gap:7px;font-size:12px;padding:4px 11px;border-radius:12px;border:1px solid transparent;line-height:1.4;}
.dot-status i{width:8px;height:8px;border-radius:50%;margin-right: 10px}
.dot-status.run{color:var(--run);background:rgba(46,255,193,.16);border-color:rgba(46,255,193,.45);}
.dot-status.run i{background:var(--run);box-shadow:0 0 8px var(--run);animation:blink 1.2s infinite;margin-right: 10px}
.dot-status.wait{color:var(--wait);background:rgba(255,176,32,.16);border-color:rgba(255,176,32,.45);}
.dot-status.wait i{background:var(--wait);box-shadow:0 0 8px var(--wait);margin-right: 10px}
.dot-status.off{color:var(--off);background:rgba(107,124,147,.20);border-color:rgba(107,124,147,.42);}
.dot-status.off i{background:var(--off);margin-right: 10px}

.col-devices{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);grid-template-rows:repeat(4,1fr);gap:14px;}
.col-devices .panel{padding:14px 16px;}
.col-devices .device .dh{margin-bottom:10px;}
.col-devices .device .dh .nm{font-size:15px;align-items:center;white-space:nowrap;color: #e8f4ff}
.col-devices .device .dh .nm .gi{width:4px;height:16px;border-radius:1px;background:var(--accent);box-shadow:0 0 8px var(--accent);flex:0 0 4px;margin-right: 10px}
.col-devices .device .dh .nm .gi svg{display:none;}
.col-devices .device .dh .nm .model{font-size:10px;font-weight:400;margin-left: 10px}
.col-devices .device .dh .dot-status{white-space:nowrap;flex:0 0 auto;}
.col-devices .device .mtr.c23{gap:10px;}
.col-devices .device .mtr .it .vl{font-size:18px;white-space:nowrap;}
.col-devices .device .mtr .it .vl small{font-size:11px;}
.col-devices > .panel:nth-child(1){grid-column:1;grid-row:1;}
.col-devices > .panel:nth-child(2){grid-column:1;grid-row:2;}
.col-devices > .panel:nth-child(3){grid-column:2;grid-row:1;}
.col-devices > .panel:nth-child(4){grid-column:2;grid-row:2;}
.col-devices > .panel:nth-child(5){grid-column:2;grid-row:3;}
.col-devices > .panel:nth-child(6){grid-column:1;grid-row:3;}
.col-devices > .panel:nth-child(7){grid-column:1;grid-row:4;}
.col-devices > .panel:nth-child(8){grid-column:2;grid-row:4;}

</style>
